import { Box } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import Map, { GeolocateControl, Marker } from 'react-map-gl/mapbox';

import { DEFAULT_INSTANCE_ZOOM, defaultCoords } from './mapConstants';
import { toLngLatTuple } from './mapUtils';

import type { GeolocateControl as MapboxGeolocateControl, MapMouseEvent } from 'mapbox-gl';
import type { ReactNode, Ref } from 'react';
import type { MapRef } from 'react-map-gl/mapbox';

export interface BaseMapMarker {
    anchor?: mapboxgl.Anchor;
    coordinates: [number, number];
    id: string;
    render?: ReactNode;
}

export interface BaseMapProps {
    bearing?: number;
    center?: [number, number];
    children?: ReactNode;
    cursor?: string;
    geolocateRef?: Ref<MapboxGeolocateControl>;
    height?: string | number;
    interactive?: boolean;
    interactiveLayerIds?: string[];
    mapboxAccessToken?: string;
    mapRef?: Ref<MapRef>;
    mapStyle?: string;
    markers?: BaseMapMarker[];
    onClick?: (event: MapMouseEvent) => void;
    onGeolocate?: (coords: [number, number]) => void;
    onGeolocateError?: (error: unknown) => void;
    onLoad?: () => void;
    onMouseEnter?: (event: MapMouseEvent) => void;
    onMouseLeave?: (event: MapMouseEvent) => void;
    onMouseMove?: (event: MapMouseEvent) => void;
    renderUserLocationMarker?: (coords: [number, number]) => ReactNode;
    showGeolocateControl?: boolean;
    trackUserLocation?: boolean;
    width?: string | number;
    zoom?: number;
}

const fallbackCenter = toLngLatTuple(defaultCoords) ?? [0, 0];

export default function BaseMap({
    bearing = 0,
    center = fallbackCenter,
    children,
    cursor = 'auto',
    geolocateRef,
    height = '100%',
    interactive = true,
    interactiveLayerIds,
    mapboxAccessToken,
    mapRef,
    mapStyle = 'mapbox://styles/mapbox/streets-v12',
    markers,
    onClick,
    onGeolocate,
    onGeolocateError,
    onLoad,
    onMouseEnter,
    onMouseLeave,
    onMouseMove,
    renderUserLocationMarker,
    showGeolocateControl = false,
    trackUserLocation = false,
    width = '100%',
    zoom = DEFAULT_INSTANCE_ZOOM,
}: Readonly<BaseMapProps>) {
    const initialViewState = useMemo(() => {
        return {
            bearing,
            latitude: center[1],
            longitude: center[0],
            zoom,
        };
    }, [bearing, center, zoom]);

    const [userLocation, setUserLocation] = useState<[number, number] | undefined>(undefined);

    const handleGeolocate = useCallback(
        (event: GeolocationPosition) => {
            const next: [number, number] = [event.coords.longitude, event.coords.latitude];
            setUserLocation(next);
            onGeolocate?.(next);
        },
        [onGeolocate],
    );

    const handleGeolocateError = useCallback(
        (error: unknown) => {
            onGeolocateError?.(error);
        },
        [onGeolocateError],
    );

    const userLocationMarker = useMemo(() => {
        if (!userLocation) {
            return null;
        }
        return (
            <Marker anchor="center" latitude={userLocation[1]} longitude={userLocation[0]} offset={[0, 0]}>
                {renderUserLocationMarker ? (
                    renderUserLocationMarker(userLocation)
                ) : (
                    <Box
                        sx={{
                            backgroundColor: '#1C4E71',
                            border: '2px solid #ffffff',
                            borderRadius: '50%',
                            height: 14,
                            width: 14,
                        }}
                    />
                )}
            </Marker>
        );
    }, [renderUserLocationMarker, userLocation]);

    return (
        <Box sx={{ height, position: 'relative', width }}>
            <Map
                boxZoom={interactive}
                cursor={interactive ? cursor : 'auto'}
                doubleClickZoom={interactive}
                dragPan={interactive}
                dragRotate={interactive}
                initialViewState={initialViewState}
                interactiveLayerIds={interactiveLayerIds}
                keyboard={interactive}
                mapboxAccessToken={mapboxAccessToken ?? process.env.EXPO_PUBLIC_MAPBOX_ACCESS_TOKEN}
                mapStyle={mapStyle}
                onClick={onClick}
                onLoad={onLoad}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onMouseMove={onMouseMove}
                ref={mapRef}
                reuseMaps
                scrollZoom={interactive}
                style={{ height: '100%', width: '100%' }}
                touchZoomRotate={interactive}
            >
                {children}
                {markers?.map((marker) => (
                    <Marker
                        anchor={marker.anchor ?? 'center'}
                        key={marker.id}
                        latitude={marker.coordinates[1]}
                        longitude={marker.coordinates[0]}
                    >
                        {marker.render ?? (
                            <Box
                                sx={{
                                    backgroundColor: '#1C4E71',
                                    border: '2px solid #ffffff',
                                    borderRadius: '50%',
                                    height: 12,
                                    width: 12,
                                }}
                            />
                        )}
                    </Marker>
                ))}
                {showGeolocateControl ? (
                    <GeolocateControl
                        fitBoundsOptions={{ maxZoom: 15 }}
                        onError={handleGeolocateError}
                        onGeolocate={handleGeolocate}
                        position="top-left"
                        positionOptions={{ enableHighAccuracy: true, maximumAge: 30_000, timeout: 6000 }}
                        ref={geolocateRef}
                        showUserLocation
                        trackUserLocation={trackUserLocation}
                    />
                ) : null}
                {userLocationMarker}
            </Map>
        </Box>
    );
}
